import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useConfig } from '../../../config';
import { useTranslation } from '../../../i18n/i18n';
import { Res, useApi } from '../../../state/api';
import { useSession } from '../../../state/session';
import { ApiDeliveryAddress, ApiList, ApiSchedule, DeliveryTypeCompany } from '../../../types';
import { AlertMessages, useAlerts } from '../../AlertMessages/AlertMessages';
import { ContentCard } from '../../ContentCard/ContentCard';
import { AddressSummary } from '../../Form/AddressSummary';
import { Button } from '../../Form/Button';
import { CheckoutSelectDeliveryAddress } from '../../Form/CheckoutSelectDeliveryAddress';
import { DeliveryAddressInput } from '../../Form/DeliveryAddressInput';
import { useFormValidationContainer } from '../../Form/FormValidatorContainer';
import { Icon, close } from '../../Icon/Icon';
import { Modal } from '../../Modal/Modal';
import { WITH_CREDENTIALS } from '../../utils';
import { WizardStepLabel } from '../WizardStepLabel';
import { WizardControl } from '../wizard';
import './EditScheduleAddress.css';

type DeliveryAddressPayload = Omit<ApiDeliveryAddress, 'id' | 'primary' | 'lastUsed'>;
type PatchOp = { op: 'replace'; path: string; value: string | number };

export const EditScheduleAddress = ({
	list,
	schedule,
	onUpdate,
	selectedDeliveryAddress,
	setSelectedDeliveryAddress,
	wizard,
	onAddressChosen,
	dialogOnly,
}: {
	list: ApiList;
	/* the draft card lists the steps itself and wants nothing from this but the picker */
	dialogOnly?: boolean;
	/* missing while the wizard asks for an address for a subscription that has no schedule yet */
	schedule?: ApiSchedule;
	onUpdate: (address?: ApiDeliveryAddress) => void;
	selectedDeliveryAddress: string;
	setSelectedDeliveryAddress: React.Dispatch<React.SetStateAction<string>>;
	/* set while the wizard is on this step, which is also what holds the picker open */
	wizard?: WizardControl;
	/* where the choice goes when there is no schedule to write it to: on to the schedule step */
	onAddressChosen?: (address: ApiDeliveryAddress) => void;
}): JSX.Element => {
	const t = { ...useTranslation().subscriptions, generic: useTranslation().generic, form: useTranslation().form };
	const { data: deliveryAddresses, mutate: mutateDeliveryAddresses } = useApi(Res.deliveryAddresses, {});
	const form = useFormValidationContainer();
	const { api } = useConfig();
	const { user } = useSession();
	const deliveryType: DeliveryTypeCompany = 'door_delivery';

	const inWizard = !!wizard;
	const [addressSaving, setAddressSaving] = useState(false);
	const alerts = useAlerts();
	const [addressPickerOpen, setAddressPickerOpen] = useState(false);
	const [showAddressForm, setShowAddressForm] = useState(false);
	const [editingDeliveryAddress, setEditingDeliveryAddress] = useState<ApiDeliveryAddress | undefined>(undefined);
	const addressFormRef = useRef<HTMLDivElement>(null);
	const selectedAddress =
		schedule?.deliveryAddress ??
		deliveryAddresses?.find((item) => item.id !== undefined && String(item.id) === selectedDeliveryAddress);

	const updateDeliveryAddress = async (addressId: string, known?: ApiDeliveryAddress): Promise<void> => {
		if (!addressId) return;
		setSelectedDeliveryAddress(addressId);
		/*
		 * `known` is for an address the caller has just created: `deliveryAddresses` is the list this
		 * render closed over, so it does not hold it however recently it was revalidated, and
		 * `editingDeliveryAddress` is only set when an existing one is being edited. Without it the
		 * card is told the schedule still has no address and goes on asking for one.
		 */
		const nextAddress =
			known ??
			deliveryAddresses?.find((item) => item.id !== undefined && String(item.id) === addressId) ??
			(editingDeliveryAddress && String(editingDeliveryAddress.id) === addressId
				? editingDeliveryAddress
				: undefined);

		/*
		 * The address is a column on the schedule, so a subscription without one has nowhere to put
		 * it yet. In the wizard the choice travels on to the schedule step, which saves both at once.
		 */
		if (!schedule) {
			if (nextAddress) onAddressChosen?.(nextAddress);
			return;
		}

		if (String(schedule.deliveryAddress?.id) === addressId) return;

		const payload = {
			...schedule,
			customerId: user?.id,
			deliveryAddressId: nextAddress?.id ?? addressId,
		};

		await axios.patch(`${api}me/lists/${list.id}/schedule.json`, payload, WITH_CREDENTIALS);

		/*
		 * Say so upwards. The patch alone left the shared schedule cache without an address, so the
		 * card went on asking for one the customer had just chosen - until some later revalidation
		 * happened to come along. onUpdate was only called when an existing address was edited, not
		 * when one was picked, which is the ordinary way through.
		 */
		onUpdate(nextAddress);
	};

	const buildPatch = (original: DeliveryAddressPayload, changed: DeliveryAddressPayload): PatchOp[] =>
		(Object.keys(original) as (keyof DeliveryAddressPayload)[])
			.filter((key) => changed[key] !== original[key] && changed[key] !== undefined)
			.map((key) => ({
				op: 'replace',
				path: '/' + String(key),
				value: changed[key] as string | number,
			}));

	const mapDeliveryAddressToPayload = (
		address: DeliveryAddressPayload | ApiDeliveryAddress
	): DeliveryAddressPayload => ({
		company: address.company,
		careOf: address.careOf,
		firstname: address.firstname,
		lastname: address.lastname,
		street: address.street,
		doorCode: address.doorCode,
		city: address.city,
		zip: address.zip,
		phone: address.phone,
	});

	const buildDeliveryAddressPayload = (): DeliveryAddressPayload => ({
		company: form.deliveryCompany.value,
		careOf: form.deliveryCareOf.value,
		firstname: form.deliveryFirstname.value,
		lastname: form.deliveryLastname.value,
		street: form.deliveryStreet.value,
		doorCode: form.deliveryDoorCode.value,
		city: form.deliveryCity.value,
		zip: form.deliveryZip.value,
		phone: form.deliveryPhone.value,
	});

	/*
	 * A customer with no saved address has nothing to pick from, so the wizard goes straight to the
	 * form - but only once the addresses are in, or the form flickers past on the way to the list.
	 */
	const addressCount = deliveryAddresses?.length;
	useEffect(() => {
		if (inWizard && addressCount !== undefined) setShowAddressForm(addressCount === 0);
	}, [inWizard, addressCount]);

	const openAddressModal = () => {
		if (!selectedDeliveryAddress && schedule?.deliveryAddress?.id !== undefined) {
			const address = deliveryAddresses?.find(
				(item) => item.id !== undefined && String(item.id) === selectedDeliveryAddress
			);
			setEditingDeliveryAddress(address);
			setSelectedDeliveryAddress(String(schedule.deliveryAddress.id));
		} else {
			setEditingDeliveryAddress(undefined);
		}
		setShowAddressForm(false);
		setAddressPickerOpen(true);
	};

	const closeAddressModal = () => {
		setAddressPickerOpen(false);
		setShowAddressForm(false);
		setEditingDeliveryAddress(undefined);

		if (wizard) wizard.onAbandon();
	};

	const selectAddress = (addressId: string) => {
		const nextAddress =
			deliveryAddresses?.find((item) => item.id !== undefined && String(item.id) === addressId) ??
			(editingDeliveryAddress && String(editingDeliveryAddress.id) === addressId
				? editingDeliveryAddress
				: undefined);
		if (!nextAddress) return;
		setEditingDeliveryAddress(addressId ? nextAddress : undefined);
		form.fillAddressForm(nextAddress);
		setShowAddressForm(false);
		setSelectedDeliveryAddress(addressId);
	};

	const startNewAddress = () => {
		setEditingDeliveryAddress(undefined);
		setSelectedDeliveryAddress('');
		form.resetAddressForm();
		setShowAddressForm(true);
		setAddressPickerOpen(true);
	};

	const startEditAddress = () => {
		const address =
			deliveryAddresses?.find((item) => item.id !== undefined && String(item.id) === selectedDeliveryAddress) ??
			selectedAddress;
		if (!address) return;
		setEditingDeliveryAddress(address);
		form.fillAddressForm(address);
		setShowAddressForm(true);
	};

	const handleSelectSavedAddress = async () => {
		if (!selectedDeliveryAddress) return;

		alerts.clear();

		try {
			await updateDeliveryAddress(selectedDeliveryAddress);
		} catch (e) {
			// the picker stays open: closing it on a write that did not happen is how it looked
			// as though pressing the button did nothing at all
			alerts.set({ type: 'error', message: t.couldNotSave });
			return;
		}

		setAddressPickerOpen(false);
		setShowAddressForm(false);
		setEditingDeliveryAddress(undefined);

		if (wizard) wizard.onDone();
	};

	const handleSaveDeliveryAddress = async () => {
		if (!form.validateDeliveryAddress({ deliveryType })) {
			if (addressFormRef.current) {
				addressFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
			return;
		}
		alerts.clear();
		setAddressSaving(true);
		const payload = buildDeliveryAddressPayload();
		let savedAddressId: ApiDeliveryAddress['id'] | undefined;
		try {
			if (editingDeliveryAddress?.id !== undefined) {
				const originalPayload = mapDeliveryAddressToPayload(editingDeliveryAddress);
				const patch = buildPatch(originalPayload, payload);
				if (patch.length > 0) {
					await axios.patch(
						`${api}me/delivery-addresses/${editingDeliveryAddress.id}.json`,
						patch,
						WITH_CREDENTIALS
					);
				}
				savedAddressId = editingDeliveryAddress.id;
			} else {
				const { data } = await axios.post(`${api}me/delivery-addresses.json`, payload, WITH_CREDENTIALS);
				savedAddressId = (data as { deliveryAddressId?: ApiDeliveryAddress['id'] })?.deliveryAddressId;
			}
			// what comes back from the revalidation holds the address that was just created, which
			// the list this render closed over never will
			const refreshed = await mutateDeliveryAddresses();

			if (editingDeliveryAddress) {
				onUpdate(editingDeliveryAddress);
			}

			if (savedAddressId !== undefined) {
				const saved = refreshed?.find(
					(item) => item.id !== undefined && String(item.id) === String(savedAddressId)
				);

				await updateDeliveryAddress(String(savedAddressId), saved);
			}
			setAddressPickerOpen(false);
			setShowAddressForm(false);
			setEditingDeliveryAddress(undefined);

			if (wizard) wizard.onDone();
		} catch (e) {
			// the form stays open with what was typed in it, rather than closing on a write that
			// did not happen
			alerts.set({ type: 'error', message: t.couldNotSave });
		} finally {
			setAddressSaving(false);
		}
	};

	return (
		<>
			{schedule && !dialogOnly && (
				<div className="schedule-setting">
					<strong className="schedule-setting-label">{t.stepAddress}</strong>
					{selectedAddress ? (
						<>
							<AddressSummary address={selectedAddress} />
							<div className="schedule-setting-links">
								<button type="button" className="text-link" onClick={openAddressModal}>
									{t.form.changeAddress}
								</button>
							</div>
						</>
					) : (
						<div className="schedule-setting-links">
							<button type="button" className="text-link" onClick={startNewAddress}>
								{t.form.selectDeliveryAddress}
							</button>
						</div>
					)}
				</div>
			)}

			<Modal
				className="content-card-modal schedule-delivery-address-modal"
				show={addressPickerOpen || inWizard}
				disableBodyScroll
				onDismiss={closeAddressModal}
				toggle={closeAddressModal}
			>
				<ContentCard className="content schedule-delivery-address-modal-content">
					<div className="schedule-delivery-address-modal-header">
						<h2 className="schedule-delivery-address-modal-title">
							<WizardStepLabel step={wizard?.step} />
							{/* in the wizard the heading is the step's own name, whichever way the picker opens */}
							{wizard
								? t.form.selectDeliveryAddress
								: selectedDeliveryAddress
									? showAddressForm
										? t.form.editAddress
										: t.form.changeAddress
									: t.form.addNewAddress}
						</h2>

						<button className="close-btn" type="button" onClick={closeAddressModal}>
							<Icon shape={{ ...close, fill: 'black' }} size={20} />
						</button>
					</div>
					<div className="modal-content">
						<AlertMessages alerts={alerts} />
						{deliveryAddresses && deliveryAddresses.length > 0 && (
							<div className="checkout-delivery-address-select">
								<CheckoutSelectDeliveryAddress
									selectedAddress={selectedDeliveryAddress}
									deliveryAddresses={deliveryAddresses}
									onSelectAddress={selectAddress}
								/>
							</div>
						)}
						{showAddressForm ? (
							<div className="checkout-delivery-address-form" ref={addressFormRef}>
								<DeliveryAddressInput
									deliveryType={deliveryType}
									deliveryAddresses={[]}
									selectedAddress={selectedDeliveryAddress}
									setSelectedAddress={setSelectedDeliveryAddress}
								/>
								<div className="checkout-delivery-address-form-actions">
									<Button type="button" disabled={addressSaving} onClick={handleSaveDeliveryAddress}>
										{addressSaving ? t.generic.saving : t.generic.save}
									</Button>
									{deliveryAddresses && deliveryAddresses.length > 0 && (
										<Button type="button" className="secondary" onClick={startNewAddress}>
											{t.form.addNewAddress}
										</Button>
									)}
									<Button type="button" className="secondary" onClick={closeAddressModal}>
										{t.generic.cancel}
									</Button>
								</div>
							</div>
						) : (
							<div className="checkout-delivery-address-form-actions">
								<Button
									type="button"
									disabled={!selectedDeliveryAddress}
									onClick={handleSelectSavedAddress}
								>
									{t.generic.select}
								</Button>
								{(selectedDeliveryAddress || selectedAddress) && (
									<Button type="button" className="secondary" onClick={startEditAddress}>
										{t.form.editAddress}
									</Button>
								)}
								<Button type="button" className="secondary" onClick={startNewAddress}>
									{t.form.addNewAddress}
								</Button>
								<Button type="button" className="secondary" onClick={closeAddressModal}>
									{t.generic.cancel}
								</Button>
							</div>
						)}
					</div>
				</ContentCard>
			</Modal>
		</>
	);
};
